/**
 * QUIPWA — AuthSession
 *
 * The Google session as an explicit state machine. The key rule: **the session
 * is a resource for syncing, never a gate for using the app.** All data is
 * local; when the session is missing or expired the app keeps working and the
 * UI offers to reconnect.
 *
 *   signed_out ──signIn──► (Google) ──callback──► active
 *        ▲                                         │  │
 *        └─────────────signOut──────────────────────┘  │ token expires / 401
 *                                                       ▼
 *   needs_consent ◄── Drive scope not granted ──── expired ──reconnect──► active
 *        │                                                (prompt=none, falls
 *        └──grantPermissions──► (Google, consent) ──► active   back to the screen)
 *
 *   account_mismatch: Google returned a different account than the one this
 *   device's data belongs to. The user chooses: switch (wipe local data) or
 *   go back.
 *
 * See docs/modulos/autenticacion.md and ADR 0004.
 */

import { create } from 'zustand'
import {
  DEFAULT_SCOPES, OAUTH_FRAGMENT_KEY, OAUTH_SEARCH_KEY, REQUIRED_SCOPES, SCOPES, USERINFO_ENDPOINT,
  buildAuthUrl, missingScopes, needsInteraction, parseCallback, randomState, type Prompt,
} from './oauth.ts'

export type AuthStatus = 'signed_out' | 'active' | 'expired' | 'needs_consent' | 'account_mismatch'

export interface GoogleUser {
  email: string
  name: string
  picture: string
}

interface StoredSession {
  accessToken: string
  expiresAt: number
  grantedScopes: string[]
  user: GoogleUser
}

export interface AuthState {
  status: AuthStatus
  user: GoogleUser | null
  grantedScopes: string[]
  expiresAt: number | null
  /** Set while status is account_mismatch: the account Google just returned. */
  pendingUser: GoogleUser | null
}

export type CallbackOutcome =
  | { ok: true; status: AuthStatus; returnTo: string }
  | { ok: false; reason: 'state_mismatch' | 'no_token' | 'userinfo_failed' | 'google_error'; detail?: string }

const SESSION_KEY = 'quipwa:auth'
const OWNER_KEY = 'quipwa:owner'
const PENDING_KEY = 'quipwa:auth:pending'
/** Treat tokens as expired this long before Google does, to avoid mid-request expiry. */
const EXPIRY_MARGIN_MS = 60_000

interface PendingRedirect {
  state: string
  mode: 'interactive' | 'silent'
  returnTo: string
  scopes: string[]
}

export interface AuthSessionDeps {
  clientId: string | undefined
  redirectUri: () => string
  navigate: (url: string) => void
  now: () => number
  fetch: typeof fetch
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  sessionStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

function readJSON<T>(storage: Pick<Storage, 'getItem'>, key: string): T | null {
  try {
    const raw = storage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export class AuthSession {
  private readonly deps: AuthSessionDeps
  private session: StoredSession | null
  /** The redirect being (or already) completed on this page load; see completeRedirect. */
  private redirectOutcome: Promise<CallbackOutcome> | null = null
  readonly store = create<AuthState>(() => ({
    status: 'signed_out',
    user: null,
    grantedScopes: [],
    expiresAt: null,
    pendingUser: null,
  }))

  constructor(deps: AuthSessionDeps) {
    this.deps = deps
    this.session = this.load()
    this.publish()
  }

  // ── queries ───────────────────────────────────────────────────────────────

  get state(): AuthState {
    return this.store.getState()
  }

  /** A valid access token, or null (and the status becomes `expired`). Never signs out. */
  async getAccessToken(): Promise<string | null> {
    const s = this.session
    if (!s) return null
    if (this.state.status === 'needs_consent' || this.state.status === 'account_mismatch') return null
    if (this.deps.now() >= s.expiresAt - EXPIRY_MARGIN_MS) {
      this.setStatus('expired')
      return null
    }
    return s.accessToken
  }

  hasScope(scope: string): boolean {
    return this.state.grantedScopes.includes(scope)
  }

  /** Email this device's local data belongs to, if any account ever signed in. */
  get ownerEmail(): string | null {
    return this.deps.storage.getItem(OWNER_KEY)
  }

  // ── transitions triggered by the user ─────────────────────────────────────

  /** First sign-in (or after signing out): Google's account picker. */
  signIn(returnTo = '/'): void {
    this.redirect({ mode: 'interactive', prompt: 'select_account', returnTo, scopes: [...DEFAULT_SCOPES] })
  }

  /**
   * Get a fresh token for the same account. Tries `prompt=none` first (Google
   * answers immediately when the account is still signed in and consented);
   * if Google needs the user, the callback falls back to the normal screen.
   */
  reconnect(returnTo = '/'): void {
    this.redirect({ mode: 'silent', prompt: 'none', returnTo, scopes: this.wantedScopes() })
  }

  /** Ask Google again for permissions the user did not grant (Drive, Tasks…). */
  grantPermissions(extraScopes: string[] = [], returnTo = '/'): void {
    this.redirect({ mode: 'interactive', prompt: 'consent', returnTo, scopes: [...this.wantedScopes(), ...extraScopes] })
  }

  /** Forget the token. Local data stays unless `wipeLocalData` is handled by the caller. */
  signOut(): void {
    this.session = null
    this.deps.storage.removeItem(SESSION_KEY)
    this.store.setState({ status: 'signed_out', user: null, grantedScopes: [], expiresAt: null, pendingUser: null })
  }

  /** Account mismatch resolved by switching: the caller has wiped local data. */
  acceptPendingAccount(): void {
    const pending = this.state.pendingUser
    if (!pending || !this.session) return
    this.deps.storage.setItem(OWNER_KEY, pending.email)
    this.session = { ...this.session, user: pending }
    this.persist()
    this.store.setState({ pendingUser: null })
    this.publish()
  }

  /** Account mismatch resolved by going back: drop the other account's token. */
  rejectPendingAccount(): void {
    this.signOut()
  }

  // ── transitions triggered by the system ───────────────────────────────────

  /** Drive answered 401: the token is no longer valid. */
  markUnauthorized(): void {
    if (this.session && this.state.status === 'active') this.setStatus('expired')
  }

  /** Drive answered "insufficient scopes". */
  markInsufficientScope(): void {
    if (this.session) this.setStatus('needs_consent')
  }

  /**
   * Finish the redirect that main.tsx parked in sessionStorage, once.
   *
   * Asking twice returns the same outcome instead of starting again. The
   * callback page asks from an effect, and React StrictMode runs effects twice
   * in development. The first run used to consume the token, and its result
   * was dropped by the simulated unmount; the second run found nothing and
   * showed "could not connect" over a sign-in that had worked.
   *
   * A new fragment in storage (a later redirect) starts a new attempt.
   */
  completeRedirect(): Promise<CallbackOutcome> {
    const store = this.deps.sessionStorage
    const hash = store.getItem(OAUTH_FRAGMENT_KEY)
    const search = store.getItem(OAUTH_SEARCH_KEY)
    if (hash === null && search === null && this.redirectOutcome) return this.redirectOutcome
    store.removeItem(OAUTH_FRAGMENT_KEY)
    store.removeItem(OAUTH_SEARCH_KEY)
    this.redirectOutcome = this.handleCallback(hash ?? '', search ?? '')
    return this.redirectOutcome
  }

  /**
   * Handle the redirect back from Google. Returns where to go next.
   * A silent attempt that needs the user turns into an interactive one (this
   * navigates away again and the promise never settles).
   */
  async handleCallback(hash: string, search: string): Promise<CallbackOutcome> {
    const pending = readJSON<PendingRedirect>(this.deps.sessionStorage, PENDING_KEY)
    const result = parseCallback(hash, search)

    if (result.kind === 'error') {
      if (pending && pending.mode === 'silent' && needsInteraction(result.error)) {
        this.redirect({ mode: 'interactive', prompt: 'select_account', returnTo: pending.returnTo, scopes: pending.scopes })
        return new Promise(() => undefined)
      }
      this.deps.sessionStorage.removeItem(PENDING_KEY)
      return { ok: false, reason: 'google_error', detail: result.error }
    }
    if (result.kind === 'empty') return { ok: false, reason: 'no_token' }
    if (!pending || result.state !== pending.state) return { ok: false, reason: 'state_mismatch' }
    this.deps.sessionStorage.removeItem(PENDING_KEY)

    let user: GoogleUser
    try {
      const res = await this.deps.fetch(USERINFO_ENDPOINT, { headers: { Authorization: `Bearer ${result.accessToken}` } })
      if (!res.ok) return { ok: false, reason: 'userinfo_failed', detail: `HTTP ${res.status}` }
      const u = (await res.json()) as { email: string; name?: string; picture?: string }
      user = { email: u.email, name: u.name ?? u.email, picture: u.picture ?? '' }
    } catch (err) {
      return { ok: false, reason: 'userinfo_failed', detail: err instanceof Error ? err.message : String(err) }
    }

    // Google may not report scopes on every response; fall back to what we asked for.
    const granted = result.grantedScopes.length > 0 ? result.grantedScopes : pending.scopes
    this.session = {
      accessToken: result.accessToken,
      expiresAt: this.deps.now() + result.expiresInSeconds * 1000,
      grantedScopes: granted,
      user,
    }
    this.persist()

    const owner = this.ownerEmail
    if (owner && owner.toLowerCase() !== user.email.toLowerCase()) {
      this.store.setState({ status: 'account_mismatch', pendingUser: user })
      return { ok: true, status: 'account_mismatch', returnTo: pending.returnTo }
    }
    if (!owner) this.deps.storage.setItem(OWNER_KEY, user.email)

    this.publish()
    return { ok: true, status: this.state.status, returnTo: pending.returnTo }
  }

  /** Subscribe to state changes. */
  subscribe(listener: (state: AuthState) => void): () => void {
    return this.store.subscribe(listener)
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private wantedScopes(): string[] {
    const keep = this.state.grantedScopes.includes(SCOPES.tasks) ? [SCOPES.tasks] : []
    return [...DEFAULT_SCOPES, ...keep]
  }

  private redirect(input: { mode: PendingRedirect['mode']; prompt: Prompt; returnTo: string; scopes: string[] }): void {
    if (!this.deps.clientId) {
      console.error('[Auth] VITE_GOOGLE_CLIENT_ID is not set')
      return
    }
    const state = randomState()
    const pending: PendingRedirect = { state, mode: input.mode, returnTo: input.returnTo, scopes: input.scopes }
    this.deps.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
    this.deps.navigate(
      buildAuthUrl({
        clientId: this.deps.clientId,
        redirectUri: this.deps.redirectUri(),
        scopes: input.scopes,
        state,
        prompt: input.prompt,
        loginHint: this.session?.user.email ?? this.ownerEmail,
      })
    )
  }

  private load(): StoredSession | null {
    const stored = readJSON<StoredSession>(this.deps.storage, SESSION_KEY)
    if (stored) return stored
    // Migrate the previous version's keys once.
    const token = this.deps.storage.getItem('wf_access_token')
    const exp = Number(this.deps.storage.getItem('wf_expires_at') ?? '0')
    const user = readJSON<GoogleUser>(this.deps.storage, 'wf_user')
    for (const k of ['wf_access_token', 'wf_expires_at', 'wf_user', 'google_refresh_token']) this.deps.storage.removeItem(k)
    if (!token || !user) return null
    const migrated: StoredSession = { accessToken: token, expiresAt: exp, grantedScopes: [SCOPES.driveFile, SCOPES.email, SCOPES.tasks], user }
    this.session = migrated
    this.persist()
    if (!this.ownerEmail) this.deps.storage.setItem(OWNER_KEY, user.email)
    return migrated
  }

  private persist(): void {
    if (this.session) this.deps.storage.setItem(SESSION_KEY, JSON.stringify(this.session))
  }

  /** Derive the status from the stored session. */
  private publish(): void {
    const s = this.session
    if (!s) {
      this.store.setState({ status: 'signed_out', user: null, grantedScopes: [], expiresAt: null })
      return
    }
    const status: AuthStatus =
      missingScopes(s.grantedScopes, REQUIRED_SCOPES).length > 0 ? 'needs_consent'
      : this.deps.now() >= s.expiresAt - EXPIRY_MARGIN_MS ? 'expired'
      : 'active'
    this.store.setState({ status, user: s.user, grantedScopes: s.grantedScopes, expiresAt: s.expiresAt })
  }

  private setStatus(status: AuthStatus): void {
    if (this.state.status !== status) this.store.setState({ status })
  }
}
